/**
 * Responsibility: Implement logic cache behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// ENGINE/LOGIC-CACHE.TS — Logic function cache
// Caches AI-generated logic functions + metadata per save to
// avoid redundant API calls for repeated normalized action signatures.
//
// Storage: in-memory Map (fast) + Firestore (persistent).
// Firestore path: artifacts/{appId}/users/{uid}/logic-cache/{saveId}
// ============================================================

import type { LogicForgeMeta } from "@anyquest/shared-core"
import { ActionCategory, loadLogicCache as backendLoadLogicCache, writeLogicCache as backendWriteLogicCache } from "@anyquest/shared-core"
export interface CacheEntry {
  fn: string
  meta: LogicForgeMeta
}

const FALLBACK_META: LogicForgeMeta = { cacheable: false, category: ActionCategory.other }
// In-memory cache — cleared when game session ends
const memCache = new Map<string, CacheEntry>()
let uid = ''
let saveId = ''
const MAX_ENTRIES = 250
function normalizeKey(cacheKey: string): string {
  return String(cacheKey ?? '').toLowerCase().trim().slice(0, 512)
}

function isCacheEntry(value: unknown): value is CacheEntry {
  return !!value
    && typeof value === 'object'
    && typeof (value as CacheEntry).fn === 'string'
    && typeof (value as CacheEntry).meta === 'object'
}

const META_ENV = ((import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env) ?? {}
const E2E_MOCK_MODE = !META_ENV.PROD && META_ENV.VITE_E2E_MOCK_MODE === '1'

/** Call this when a game session starts (new game or load). */
export async function initLogicCache(newUid: string, newSaveId: string): Promise<void> {
  uid = newUid
  saveId = newSaveId
  memCache.clear()
  if (E2E_MOCK_MODE) return
  try {
    const raw = await backendLoadLogicCache(uid, saveId)
    if (!raw) return
    for (const [k, v] of Object.entries(raw)) {
      // Backward compat: old format stored plain string fn
      if (typeof v === 'string') {
        memCache.set(k, { fn: v, meta: FALLBACK_META })
      } else if (isCacheEntry(v)) {
        memCache.set(k, v)
      }
    }
    console.log(`[LogicCache] Loaded ${memCache.size} cached entries.`)
  } catch (err) {
    console.warn('[LogicCache] Failed to load from Supabase:', err)
  }
}

/** Look up a cached entry for a normalized action signature. */
export function getCachedEntry(cacheKey: string): CacheEntry | undefined {
  return memCache.get(normalizeKey(cacheKey))
}

/** Store a logic entry for a normalized action signature and persist to Supabase. */
export async function cacheEntry(cacheKey: string, fn: string, meta: LogicForgeMeta): Promise<void> {
  if (!uid || !saveId) return
  const key = normalizeKey(cacheKey)
  memCache.set(key, { fn, meta })
  if (memCache.size > MAX_ENTRIES) {
    const toDelete = [...memCache.keys()].slice(0, memCache.size - MAX_ENTRIES)
    toDelete.forEach(k => memCache.delete(k))
  }

  void backendWriteLogicCache(uid, saveId, Object.fromEntries(memCache))
    .catch(error => {
      console.warn('[LogicCache] Persist failed:', error)
    })
}

/** Clear the in-memory cache (call on sign-out or new game). */
export function clearLogicCache(): void {
  memCache.clear()
  uid = ''
  saveId = ''
}
